import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cart } from "./cart";
import { listInput, listPage } from "./list";
import { Cents } from "./money";
import { OrderLine, OrderStatus, PaymentStatus, ShipmentStatus, ShopOrder } from "./order";
import { VatBasis } from "./price";
import { ShopPhoto } from "./shop-catalog";

/**
 * The signed-in customer's orders in the shop (#105: "Bestelhistorie", #106: "Opnieuw bestellen").
 * Every endpoint needs the customer session (401 `CustomerNotSignedIn`).
 *
 * - Only the customer's own orders: the ones placed while signed in. An order placed as a guest,
 *   even with the same email address, belongs to that guest's customer and is not listed.
 * - An order is addressed by its number (`CBO-100000`). Someone else's order, or a number that
 *   does not exist, is `AccountOrderNotFound` (404), the same answer for both.
 */

/** The page of the history: newest first. Only `page` and `pageSize`, as every list takes them. */
export const AccountOrderListInput = listInput({ sort: ["placedAt"], defaultSort: "placedAt" }).pick({
	page: true,
	pageSize: true,
});
export type AccountOrderListInput = z.infer<typeof AccountOrderListInput>;

/** One order in the history. */
export const AccountOrderSummary = z.object({
	orderNumber: z.string(),
	placedAt: z.iso.datetime(),
	paymentStatus: PaymentStatus,
	shipmentStatus: ShipmentStatus,
	orderStatus: OrderStatus,
	/** What the order came to, including VAT (the order's `totals.toPay`). */
	total: Cents,
	/** The side of VAT the order's prices are on; `total` is always including VAT. */
	priceDisplay: VatBasis,
	/** How many lines (products) the order has, not units. */
	lineCount: z.number().int(),
});
export type AccountOrderSummary = z.infer<typeof AccountOrderSummary>;

export const AccountOrderList = listPage(AccountOrderSummary);
export type AccountOrderList = z.infer<typeof AccountOrderList>;

/** PostNL, DHL, DPD or Bpost (spec, "Vervoerders"), as the orders admin stores it. */
export const AccountOrderTracking = z.object({
	carrier: z.string(),
	code: z.string(),
	/** The carrier's track-and-trace page for this parcel; `null` for a carrier we have no link for. */
	url: z.url().nullable(),
});
export type AccountOrderTracking = z.infer<typeof AccountOrderTracking>;

/** An order's line in the account: the snapshot, plus where the product is in the shop today. */
export const AccountOrderLine = OrderLine.extend({
	/**
	 * The product's slug (`/product/{slug}`) while the line's variant is for sale (active, not
	 * deleted, of an active product); `null` once it is not. The shop reads the size label from it.
	 */
	productSlug: z.string().nullable(),
});
export type AccountOrderLine = z.infer<typeof AccountOrderLine>;

/** A credit invoice of the order (a refund, or cancelling it on account), as the customer downloads it. */
export const AccountCreditInvoice = z.object({
	/** `CBC-100000`. */
	number: z.string(),
	/** What it credits, including VAT, as the credit invoice has it: negative. */
	totalIncl: Cents,
	/** The pdf: the signed link the credit invoice mail carries. */
	url: z.string(),
});
export type AccountCreditInvoice = z.infer<typeof AccountCreditInvoice>;

/** An order as the customer sees it in their account: the snapshot, plus tracking and invoice. */
export const AccountOrder = ShopOrder.extend({
	lines: z.array(AccountOrderLine),
	/** Once shipped with a tracking code; `null` before. */
	tracking: AccountOrderTracking.nullable(),
	/**
	 * Where the invoice pdf downloads (`invoice` below), once the order has one; `null` before. Only
	 * with the customer's own session.
	 */
	invoiceUrl: z.string().nullable(),
	/** Its credit invoices, oldest first; empty without one. */
	creditInvoices: z.array(AccountCreditInvoice),
});
export type AccountOrder = z.infer<typeof AccountOrder>;

/** A line of the latest order as reordering it would put it in an empty cart, at today's price. */
export const ReorderPreviewLine = z.object({
	variantId: z.string(),
	productName: z.string(),
	/** The product page (`/product/{slug}`). */
	productSlug: z.string(),
	/** As the cart line's. */
	sizeLabel: z.string().nullable(),
	photo: ShopPhoto.nullable(),
	/** What the cart would hold: the order's quantity, capped at what is available now. */
	quantity: z.number().int().min(1),
	/** What the order had; more than `quantity` when stock is short. */
	orderedQuantity: z.number().int().min(1),
	/** Today's price for this customer, on `priceDisplay`, as the cart would charge it. */
	unitPrice: Cents,
	lineTotal: Cents,
	pricePerPiece: Cents.nullable(),
	pieceNoun: z.string().nullable(),
});
export type ReorderPreviewLine = z.infer<typeof ReorderPreviewLine>;

/** A line of the latest order that reordering would not add. */
export const ReorderPreviewSkipped = z.object({
	variantId: z.string(),
	productName: z.string(),
});
export type ReorderPreviewSkipped = z.infer<typeof ReorderPreviewSkipped>;

/** The holder a refill of the latest order fits on ("Jouw wisser"). */
export const ReorderPreviewHolder = z.object({
	id: z.string(),
	name: z.string(),
	singularName: z.string().nullable(),
});
export type ReorderPreviewHolder = z.infer<typeof ReorderPreviewHolder>;

/**
 * "Bestel opnieuw" on the home and in the account: the customer's latest order (not cancelled or
 * expired), as `reorder` would put it in an empty cart now. `null`: the customer has no such order.
 */
export const ReorderPreview = z
	.object({
		orderNumber: z.string(),
		placedAt: z.iso.datetime(),
		/** The side of VAT `unitPrice`, `lineTotal` and `total` are on: excl VAT for an approved business. */
		priceDisplay: VatBasis,
		/** The lines reordering adds, in the order's order. */
		lines: z.array(ReorderPreviewLine),
		/** The sum of `lines`: the cart's goods once they are in an empty cart (the amount on the button). */
		total: Cents,
		/** Deleted, inactive or of an inactive product: no longer for sale. */
		notForSale: z.array(ReorderPreviewSkipped),
		/** For sale, but none available now. */
		soldOut: z.array(ReorderPreviewSkipped),
		/** The holder the order's first refill with a fit fits on; `null` for none. */
		holder: ReorderPreviewHolder.nullable(),
	})
	.nullable();
export type ReorderPreview = z.infer<typeof ReorderPreview>;

/** An order's place in the path: `/shop/account/orders/{orderNumber}`. */
export const AccountOrderPath = z.object({ orderNumber: z.string().min(1) });

export const AccountOrderContract = {
	/** The customer's orders, newest first. */
	list: oc
		.route({ method: "GET", path: "/shop/account/orders" })
		.input(AccountOrderListInput)
		.output(AccountOrderList),
	/** One of the customer's orders. Errors: `AccountOrderNotFound` (404). */
	get: oc
		.route({ method: "GET", path: "/shop/account/orders/{orderNumber}" })
		.input(AccountOrderPath)
		.output(AccountOrder),
	/**
	 * Puts the order's lines in the customer's cart, and answers the cart (as `POST /shop/cart/lines`
	 * would, at today's prices). Per line:
	 *
	 * - a variant that is deleted, inactive or of an inactive product is skipped, with a
	 *   `variantRemoved` notice (with the product name);
	 * - the quantity adds up with what the cart already holds, but never past what is available
	 *   now, as when a guest cart is merged: a capped line gets a `quantityReduced` notice with the
	 *   quantity the cart holds now; `quantity: 0` means the product is out of stock and was not
	 *   added.
	 *
	 * Errors: `AccountOrderNotFound` (404).
	 */
	reorder: oc
		.route({ method: "POST", path: "/shop/account/orders/{orderNumber}/reorder" })
		.input(AccountOrderPath)
		.output(Cart),
	/**
	 * The latest order (not cancelled or expired, newest first) as "Bestel opnieuw" shows it: today's
	 * prices on the customer's basis, the total the cart will make of it, and what would be skipped.
	 * `null` without an order. No addresses: only what the block shows.
	 */
	reorderPreview: oc.route({ method: "GET", path: "/shop/account/reorder-preview" }).output(ReorderPreview),
	/**
	 * The order's invoice as a pdf (the order's `invoiceUrl`). Errors: `InvoiceNotFound` (404): the
	 * order has no invoice yet, or is not the customer's.
	 */
	invoice: oc
		.route({ method: "GET", path: "/shop/account/orders/{orderNumber}/invoice" })
		.input(AccountOrderPath)
		.output(z.file()),
};
